import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { List } from 'react-window';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import GroupedProjectSelect from '../common/GroupedProjectSelect';
import TimeTrackerAdminHelpModal from './TimeTrackerAdminHelpModal';
import { buildProjectDisciplineOptions } from './projectDisciplineOptions';

// Row renderer for virtualized list
const VirtualRow = ({ index, style, rows, onCellEdit, onDeleteRow, onToggleActive }) => {
  const row = rows?.[index];
  if (!row) return null;
  
  const isInactive = row.is_active === false;
  const fromChangeLog = row.synced_from_change_log === true;
  
  return (
    <div style={{
      ...style,
      display: 'flex',
      alignItems: 'center',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: row.isNew ? '#f0fdf4' : (isInactive ? '#f9fafb' : (index % 2 === 0 ? 'white' : '#f9fafb')),
      opacity: isInactive ? 0.6 : 1
    }}>
      <div style={{ width: 150, padding: '8px', borderRight: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={row.op_code || ''}
          disabled={fromChangeLog}
          onChange={(e) => onCellEdit(row.id, 'op_code', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '0.875rem',
            backgroundColor: fromChangeLog ? '#f3f4f6' : 'white',
            textDecoration: isInactive ? 'line-through' : 'none'
          }}
        />
      </div>
      <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={row.op_description || ''}
          disabled={fromChangeLog}
          onChange={(e) => onCellEdit(row.id, 'op_description', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '0.875rem',
            backgroundColor: fromChangeLog ? '#f3f4f6' : 'white',
            textDecoration: isInactive ? 'line-through' : 'none',
            color: isInactive ? '#9ca3af' : '#111827'
          }}
        />
      </div>
      <div style={{ width: 100, padding: '8px', borderRight: '1px solid #e5e7eb', textAlign: 'center' }}>
        {fromChangeLog ? (
          <span style={{
            display: 'inline-block', fontSize: '0.65rem', fontWeight: 800,
            background: '#f59e0b', color: '#fff', borderRadius: 4, padding: '2px 6px',
          }}>CHANGE LOG</span>
        ) : (
          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Manual</span>
        )}
      </div>
      <div style={{ width: 80, padding: '8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
        {!row.isNew && (
          <button
            onClick={() => onToggleActive(row.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              fontSize: '0.75rem',
              color: row.is_active !== false ? '#059669' : '#9ca3af',
              padding: '2px 6px',
              borderRadius: '4px',
              width: '100%'
            }}
            title={row.is_active !== false ? 'Click to disable' : 'Click to enable'}
          >
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: row.is_active !== false ? '#10b981' : '#d1d5db',
              display: 'inline-block'
            }} />
            {row.is_active !== false ? 'Active' : 'Inactive'}
          </button>
        )}
      </div>
      <div style={{ width: 60, padding: '8px', textAlign: 'center' }}>
        <button
          onClick={() => onDeleteRow(row.id)}
          disabled={fromChangeLog}
          style={{
            background: 'none',
            border: 'none',
            color: fromChangeLog ? '#d1d5db' : '#dc2626',
            cursor: fromChangeLog ? 'not-allowed' : 'pointer',
            fontSize: '1rem'
          }}
          title={fromChangeLog ? 'Change Log extras stay for timesheet history' : 'Delete'}
        >
          🗑️
        </button>
      </div>
    </div>
  );
};

/**
 * ExtrasAdmin Component
 * Manages extra operation codes for projects within the Time Tracker Admin modal.
 * Extras are additional op codes associated with project-disciplines (e.g., 99999MWR).
 * Features: Project-discipline selector, virtualized editable table, active/inactive toggle.
 */
const ExtrasAdmin = ({ onBack }) => {
  const { instance, accounts } = useMsal();
  const { toast, showToast } = useToast();
  
  // State
  const [projects, setProjects] = useState([]);
  const [selectedOptionKey, setSelectedOptionKey] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedDisciplineCode, setSelectedDisciplineCode] = useState(null);
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedRows, setEditedRows] = useState({});
  const [newRows, setNewRows] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const listContainerRef = useRef(null);
  const [listWidth, setListWidth] = useState(800);

  // Update list width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (listContainerRef.current) {
        setListWidth(listContainerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/projects');
        if (response.data.success) {
          setProjects(response.data.projects || []);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        showToast('Failed to load projects', 'error');
      }
    };
    fetchProjects();
  }, [instance, accounts, showToast]);

  // Transform projects into project-discipline combinations for dropdown
  const projectDisciplineOptions = useMemo(
    () => buildProjectDisciplineOptions(projects),
    [projects]
  );

  // Fetch extras when project-discipline is selected
  const fetchExtras = useCallback(async () => {
    if (!selectedProjectId) {
      setExtras([]);
      return;
    }
    
    setLoading(true);
    try {
      let url = `/op-codes?jobId=${selectedProjectId}&isExtra=true`;
      if (selectedDisciplineCode) {
        url += `&disciplineCode=${encodeURIComponent(selectedDisciplineCode)}`;
      }
      const response = await authenticatedApiCall(instance, accounts, 'GET', url);
      if (response.data.success) {
        setExtras(response.data.data || []);
        setEditedRows({});
        setNewRows([]);
      }
    } catch (error) {
      console.error('Error fetching extras:', error);
      showToast('Failed to load extras', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, selectedProjectId, selectedDisciplineCode, showToast]);

  useEffect(() => {
    fetchExtras();
  }, [fetchExtras]);

  // Handle project-discipline selection
  const handleProjectChange = (optionKey) => {
    if (!optionKey) {
      setSelectedOptionKey(null);
      setSelectedProjectId(null);
      setSelectedDisciplineCode(null);
      return;
    }
    
    setSelectedOptionKey(optionKey);
    const selected = projectDisciplineOptions.find(opt => opt.optionKey === optionKey);
    if (selected) {
      setSelectedProjectId(selected.project_id);
      setSelectedDisciplineCode(selected.discipline_code);
    }
  };

  // Handle cell edit
  const handleCellEdit = useCallback((id, field, value) => {
    const existing = extras.find(r => r.id === id);
    if (existing?.synced_from_change_log && (field === 'op_code' || field === 'op_description')) {
      return;
    }
    if (id.toString().startsWith('new_')) {
      setNewRows(prev => prev.map(row => 
        row.tempId === id ? { ...row, [field]: value } : row
      ));
    } else {
      setEditedRows(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value,
          id
        }
      }));
    }
  }, [extras]);

  // Add new row
  const handleAddRow = () => {
    const tempId = `new_${Date.now()}`;
    setNewRows(prev => [...prev, { tempId, op_code: '', op_description: '', is_active: true }]);
  };

  // Toggle active/inactive
  const handleToggleActive = useCallback(async (id) => {
    if (id.toString().startsWith('new_')) return;
    
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'PATCH', `/op-codes/${id}/toggle-active`
      );
      if (response.data.success) {
        const updatedRow = response.data.data;
        setExtras(prev => prev.map(row => 
          row.id === id ? { ...row, is_active: updatedRow.is_active } : row
        ));
        showToast(
          `Extra ${updatedRow.is_active ? 'activated' : 'deactivated'}`,
          'success'
        );
      }
    } catch (error) {
      console.error('Error toggling active status:', error);
      showToast('Failed to toggle status', 'error');
    }
  }, [instance, accounts, showToast]);

  // Delete row
  const handleDeleteRow = useCallback(async (id) => {
    if (id.toString().startsWith('new_')) {
      setNewRows(prev => prev.filter(row => row.tempId !== id));
      return;
    }
    
    const existing = extras.find(r => r.id === id);
    if (existing?.synced_from_change_log) {
      showToast('Change Log extras cannot be deleted here. Disable them instead.', 'error');
      return;
    }
    if (!window.confirm('Remove this extra from timesheets? It is not erased. Accounting can restore it from Audit.')) return;
    
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/op-codes/${id}`);
      if (response.data.success) {
        setExtras(prev => prev.filter(row => row.id !== id));
        showToast('Extra removed. Restore it from Audit if needed.', 'success');
      }
    } catch (error) {
      console.error('Error deleting extra:', error);
      showToast('Failed to remove extra', 'error');
    }
  }, [instance, accounts, showToast]);

  // Save all changes
  const handleSaveChanges = async () => {
    const changes = [];
    
    // Collect edited existing rows
    Object.values(editedRows).forEach(row => {
      const original = extras.find(o => o.id === row.id);
      if (original) {
        changes.push({
          id: row.id,
          op_code: row.op_code !== undefined ? row.op_code : original.op_code,
          op_description: row.op_description !== undefined ? row.op_description : original.op_description
        });
      }
    });
    
    // Collect new rows
    newRows.forEach(row => {
      if (row.op_code?.trim()) {
        changes.push({
          op_code: row.op_code,
          op_description: row.op_description
        });
      }
    });
    
    if (changes.length === 0) {
      showToast('No changes to save', 'info');
      return;
    }
    
    setSaving(true);
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'POST', '/op-codes/bulk-save',
        { jobId: selectedProjectId, disciplineCode: selectedDisciplineCode, isExtra: true, changes }
      );
      
      if (response.data.success) {
        showToast(`Saving ${changes.length} changes...`, 'info');
        setTimeout(() => {
          fetchExtras();
          showToast('Changes saved successfully', 'success');
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      showToast('Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Combine existing and new rows for display
  const allRows = useMemo(() => {
    const existingWithEdits = extras.map(row => ({
      ...row,
      ...(editedRows[row.id] || {}),
      isNew: false
    }));
    
    const newWithIds = newRows.map(row => ({
      ...row,
      id: row.tempId,
      isNew: true
    }));
    
    let combined = [...existingWithEdits, ...newWithIds];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      combined = combined.filter(row => 
        row.op_code?.toLowerCase().includes(term) ||
        row.op_description?.toLowerCase().includes(term)
      );
    }
    
    return combined;
  }, [extras, editedRows, newRows, searchTerm]);

  const hasChanges = Object.keys(editedRows).length > 0 || newRows.some(r => r.op_code?.trim());

  const itemData = useMemo(() => ({
    rows: allRows,
    onCellEdit: handleCellEdit,
    onDeleteRow: handleDeleteRow,
    onToggleActive: handleToggleActive
  }), [allRows, handleCellEdit, handleDeleteRow, handleToggleActive]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            ←
          </button>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
            Extras Management
          </h2>
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            style={{
              padding: '6px 10px', backgroundColor: '#0d9488', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600,
            }}
          >
            ❓ Help
          </button>
        </div>
        
        {/* Project-Discipline Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>Project:</label>
          <GroupedProjectSelect
            value={selectedOptionKey}
            onChange={handleProjectChange}
            options={projectDisciplineOptions}
            placeholder="Select a project..."
          />
        </div>
      </div>
      
      <div style={{
        padding: '0.65rem 1.5rem',
        background: '#fffbeb',
        borderBottom: '1px solid #fde68a',
        fontSize: '0.8rem',
        color: '#92400e',
        lineHeight: 1.45,
      }}>
        Extras are created from Change Log. Use this screen to enable or disable them on timesheets. Manual extras are still allowed for edge cases.
      </div>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 1.5rem',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: 'white'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            placeholder="Search extras..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem',
              width: '200px'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleAddRow}
            disabled={!selectedProjectId}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: selectedProjectId ? '#f59e0b' : '#e5e7eb',
              color: selectedProjectId ? 'white' : '#9ca3af',
              border: 'none',
              borderRadius: '6px',
              cursor: selectedProjectId ? 'pointer' : 'not-allowed',
              fontSize: '0.8rem',
              fontWeight: '500'
            }}
          >
            + Add Extra
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', padding: '0' }}>
        {!selectedProjectId ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6b7280'
          }}>
            Select a project to manage extras
          </div>
        ) : loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6b7280'
          }}>
            Loading...
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Table Header */}
            <div style={{
              display: 'flex',
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f3f4f6',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              <div style={{ width: 150, padding: '10px 8px', borderRight: '1px solid #e5e7eb' }}>Op Code</div>
              <div style={{ flex: 1, padding: '10px 8px', borderRight: '1px solid #e5e7eb' }}>Description</div>
              <div style={{ width: 100, padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>Source</div>
              <div style={{ width: 80, padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>Status</div>
              <div style={{ width: 60, padding: '10px 8px', textAlign: 'center' }}>Actions</div>
            </div>
            
            {/* Virtualized Table Body */}
            <div ref={listContainerRef} style={{ flex: 1 }}>
              {allRows.length === 0 ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#6b7280'
                }}>
                  No extras found. Click "+ Add Extra" to create one.
                </div>
              ) : (
                <List
                  rowComponent={VirtualRow}
                  rowCount={allRows.length}
                  rowHeight={45}
                  rowProps={itemData}
                  overscanCount={5}
                  style={{ height: 400, width: '100%' }}
                />
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {selectedProjectId && `${allRows.length} extra${allRows.length !== 1 ? 's' : ''}`}
          {hasChanges && <span style={{ color: '#f59e0b', marginLeft: '1rem' }}>• Unsaved changes</span>}
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onBack}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Back
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={!hasChanges || saving}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: hasChanges ? '#f59e0b' : '#9ca3af',
              color: 'white',
              cursor: hasChanges ? 'pointer' : 'not-allowed',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
      
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: toast.type === 'error' ? '#fee2e2' : toast.type === 'success' ? '#d1fae5' : '#dbeafe',
          color: toast.type === 'error' ? '#b91c1c' : toast.type === 'success' ? '#047857' : '#1e40af',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1100
        }}>
          {toast.message}
        </div>
      )}
      {showHelp && <TimeTrackerAdminHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default ExtrasAdmin;
